import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { cn } from '@/shared/lib/cn';

import { NAV_ITEMS } from './navItems';

/**
 * The floating bottom tab bar from the mockup.
 *
 * ONE shape, at every width. The previous build swapped in a desktop sidebar
 * above `md`, which is a navigation pattern that appears nowhere in the
 * approved design — the mockup is a phone-width column with this pill floating
 * over the content on every screen it draws. The column is centred on a wide
 * display and the bar is centred with it, rather than the app turning into a
 * different product at 768px.
 *
 * Shape taken from the reference: a white pill with a soft drop shadow,
 * detached from the bottom edge, sized to its contents rather than stretched
 * across the viewport. The active tab is a filled brand circle with the glyph
 * knocked out; inactive tabs are the glyph in brand ink on white.
 *
 * The mockup shows no text labels. Labels are still rendered for screen
 * readers (`sr-only`) — three unlabelled icons are not a navigable interface
 * with assistive tech, and dropping the visible text is a visual decision, not
 * a semantic one.
 */
export function BottomTabBar({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('nav.primary')}
      className={cn(
        // `pointer-events-none` on the full-width strip, restored on the pill:
        // the bar floats over the content, and the transparent gutters either
        // side of it must not swallow taps meant for the screen underneath.
        'pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center',
        'pb-[calc(var(--safe-bottom)+12px)]',
        className
      )}
    >
      <ul
        className={cn(
          'pointer-events-auto flex items-center gap-2 rounded-pill p-2',
          'bg-[var(--surface)] shadow-[var(--shadow-lg)]'
        )}
      >
        {NAV_ITEMS.map(({ to, labelKey, Icon }) => (
          <li key={to} className="flex">
            <NavLink
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'grid size-12 place-items-center rounded-full transition-colors duration-150',
                  'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
                  isActive
                    ? 'bg-[var(--brand)] text-[var(--on-brand)]'
                    : 'text-[var(--brand)] hover:bg-[var(--brand-subtle)]'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="size-6" strokeWidth={isActive ? 2 : 1.7} />
                  <span className="sr-only">{t(labelKey)}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
